/**
 * Lightweight analytics helper for Sachdeva Sons.
 * - Loads Google Analytics 4, Meta Pixel, and Microsoft Clarity scripts once on app mount.
 * - Provides a single API surface for tracking events to:
 *     1. Google Analytics 4 (gtag)
 *     2. Meta/Facebook Pixel (fbq)
 *     3. Internal backend (/api/analytics/event) for the admin dashboard.
 * - Mobile friendly. No external npm dependencies. Network calls are best-effort and never block the UI.
 */

import axios from 'axios';

const GA_ID = process.env.REACT_APP_GA4_ID;
const PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID;
const CLARITY_ID = process.env.REACT_APP_CLARITY_ID;
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Persistent anonymous session id for unique-visitor tracking
const sessionId = (() => {
  if (typeof window === 'undefined') return '';
  try {
    let id = localStorage.getItem('ss_visitor_id');
    if (!id) {
      id = `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
      localStorage.setItem('ss_visitor_id', id);
    }
    return id;
  } catch {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
  }
})();

let initialised = false;

const loadScript = (src, attrs = {}) => {
  return new Promise((resolve) => {
    const s = document.createElement('script');
    s.async = true;
    s.src = src;
    Object.entries(attrs).forEach(([k, v]) => s.setAttribute(k, v));
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.head.appendChild(s);
  });
};

export const initAnalytics = () => {
  if (initialised || typeof window === 'undefined') return;
  initialised = true;

  // ---- Google Analytics 4 ----
  if (GA_ID) {
    window.dataLayer = window.dataLayer || [];
    window.gtag = function () { window.dataLayer.push(arguments); };
    window.gtag('js', new Date());
    window.gtag('config', GA_ID, { send_page_view: true });
    loadScript(`https://www.googletagmanager.com/gtag/js?id=${GA_ID}`);
  }

  // ---- Meta Pixel ----
  if (PIXEL_ID) {
    /* eslint-disable */
    !function(f,b,e,v,n,t,s){
      if(f.fbq) return; n=f.fbq=function(){n.callMethod ? n.callMethod.apply(n,arguments) : n.queue.push(arguments)};
      if(!f._fbq) f._fbq=n; n.push=n; n.loaded=!0; n.version='2.0'; n.queue=[];
      t=b.createElement(e); t.async=!0; t.src=v; s=b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t,s);
    }(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
    /* eslint-enable */
    window.fbq('init', PIXEL_ID);
    window.fbq('track', 'PageView');
  }

  // ---- Microsoft Clarity ----
  if (CLARITY_ID) {
    /* eslint-disable */
    (function(c,l,a,r,i,t,y){
      c[a] = c[a] || function(){(c[a].q = c[a].q || []).push(arguments)};
      t = l.createElement(r); t.async=1; t.src='https://www.clarity.ms/tag/' + i;
      y = l.getElementsByTagName(r)[0]; y.parentNode.insertBefore(t, y);
    })(window, document, 'clarity', 'script', CLARITY_ID);
    /* eslint-enable */
  }
};

// ---- Internal tracker (best effort, fire-and-forget) ----
const internalTrack = (event_type, payload = {}) => {
  if (typeof window === 'undefined') return;
  try {
    axios.post(`${API}/analytics/event`, {
      event_type,
      session_id: sessionId,
      page: window.location.pathname,
      ...payload,
    }, { timeout: 5000 }).catch(() => {});
  } catch {}
};

// ---- Public tracking API ----
export const trackPageView = (path) => {
  if (window.gtag && GA_ID) window.gtag('event', 'page_view', { page_path: path });
  if (window.fbq) window.fbq('track', 'PageView');
  internalTrack('page_view', { metadata: { path } });
};

export const trackProductView = (product) => {
  if (!product) return;
  if (window.gtag && GA_ID) {
    window.gtag('event', 'view_item', {
      currency: 'INR',
      value: product.price,
      items: [{ item_id: String(product.id), item_name: product.name, item_category: product.category, price: product.price, quantity: 1 }],
    });
  }
  if (window.fbq) window.fbq('track', 'ViewContent', {
    content_ids: [String(product.id)],
    content_name: product.name,
    content_type: 'product',
    content_category: product.category,
    value: product.price,
    currency: 'INR',
  });
  internalTrack('product_view', { product_id: product.id, metadata: { name: product.name, price: product.price } });
};

export const trackAddToCart = (product, quantity = 1) => {
  if (!product) return;
  const value = (product.price || 0) * quantity;
  if (window.gtag && GA_ID) {
    window.gtag('event', 'add_to_cart', {
      currency: 'INR',
      value,
      items: [{ item_id: String(product.id), item_name: product.name, item_category: product.category, price: product.price, quantity }],
    });
  }
  if (window.fbq) window.fbq('track', 'AddToCart', {
    content_ids: [String(product.id)],
    content_name: product.name,
    content_type: 'product',
    value,
    currency: 'INR',
  });
  internalTrack('add_to_cart', { product_id: product.id, metadata: { name: product.name, quantity, value } });
};

export const trackCheckoutStart = (items = [], value = 0) => {
  if (window.gtag && GA_ID) {
    window.gtag('event', 'begin_checkout', {
      currency: 'INR',
      value,
      items: items.map(i => ({ item_id: String(i.id), item_name: i.name, price: i.price, quantity: i.quantity })),
    });
  }
  if (window.fbq) window.fbq('track', 'InitiateCheckout', {
    content_ids: items.map(i => String(i.id)),
    contents: items.map(i => ({ id: String(i.id), quantity: i.quantity })),
    num_items: items.reduce((n, i) => n + (i.quantity || 1), 0),
    value,
    currency: 'INR',
  });
  internalTrack('begin_checkout', { metadata: { items_count: items.length, value } });
};

export const trackPurchase = (orderId, items = [], value = 0) => {
  if (window.gtag && GA_ID) {
    window.gtag('event', 'purchase', {
      transaction_id: orderId,
      currency: 'INR',
      value,
      items: items.map(i => ({ item_id: String(i.id), item_name: i.name, price: i.price, quantity: i.quantity })),
    });
  }
  if (window.fbq) window.fbq('track', 'Purchase', {
    content_ids: items.map(i => String(i.id)),
    value,
    currency: 'INR',
    contents: items.map(i => ({ id: String(i.id), quantity: i.quantity })),
  });
  internalTrack('purchase', { metadata: { order_id: orderId, value } });
};

export const trackWhatsApp = (context = 'floating') => {
  if (window.gtag && GA_ID) window.gtag('event', 'whatsapp_click', { event_category: 'engagement', event_label: context });
  if (window.fbq) window.fbq('trackCustom', 'WhatsAppClick', { context });
  internalTrack('whatsapp_click', { metadata: { context } });
};

export const saveAbandonedCart = (data) => {
  if (typeof window === 'undefined') return;
  try {
    axios.post(`${API}/analytics/abandoned-cart`, {
      session_id: sessionId,
      ...data,
    }, { timeout: 5000 }).catch(() => {});
  } catch {}
};

export const getSessionId = () => sessionId;
